import React, { useMemo, useRef } from 'react'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { closeModal } from '@/reducers/ui/ui.redux'
import { updateSettings } from '@/reducers/qa-copilot/qa-copilot.redux'
import { deleteScorecardConfig } from '@/reducers/qa-copilot/qa-copilot.actions'
import { addPrompt, setPrompts } from '@/reducers/prompts/prompts.redux'
import {
  removePromptHelper,
  updatePromptHelper,
  withoutLegacyModel,
} from '@/reducers/prompts/prompts.helpers'
import { getStringValueFromBoolean } from '@/utils/helpers'
import {
  eligibilityModelBlocksSave,
  fallbackModelBlocksSave,
} from '@/components/forms/formik/selects/modelGating'

import { QACopilotSettingsForm } from '../forms/QACopilotSettingsForm'
import {
  eligibilityPromptHasChanges,
  findEligibilityPrompt,
  getEligibilityPromptPayload,
  getEligibilityPromptValues,
  getSettingsValidationSchema,
  getDefaultFallbackModelId,
  getDefaultQaCopilotModelId,
} from '../QACopilot.helpers'

export const QACopilotSettingsModal = ({ isCreate, isOutdatedScorecard }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { organizationid: organizationId, prompt_editor: promptEditor } = useSelector(
    (state) => state.currentUser
  )
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const { scorecardConfig, data } = useSelector((state) => state.qaCopilot)
  const {
    prompts,
    pristinePrompts,
    modelOptions: {
      qa_copilot: qaCopilotModelOptions = [],
      copilot_fallback: copilotFallbackModelOptions = [],
    } = {},
  } = useSelector((state) => state.prompts)
  const eligibilityPrompt = findEligibilityPrompt(prompts, scorecardConfig?.eligibility_prompt_id)
  const pristineEligibilityPrompt = findEligibilityPrompt(
    pristinePrompts,
    scorecardConfig?.eligibility_prompt_id
  )
  const scoresToFallback = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
    { label: 'N/A', value: 'n/a' },
  ]
  const modalId = `qaCopilot/settings`
  const isBaltoAdmin = organizationId === 1
  const isPromptEditor = isBaltoAdmin || promptEditor
  const formikDirtyRef = useRef(false)

  const defaultFallbackModelId = useMemo(
    () => getDefaultFallbackModelId(copilotFallbackModelOptions),
    [copilotFallbackModelOptions]
  )
  const defaultQaCopilotModelId = useMemo(
    () => getDefaultQaCopilotModelId(qaCopilotModelOptions),
    [qaCopilotModelOptions]
  )

  if (isEmpty(scorecardConfig?.sections)) {
    return null
  }

  if (modalId !== currentlyOpenModalId) {
    return null
  }

  const selectedScoresToFallback = scorecardConfig.scores_to_fallback?.map((score) =>
    scoresToFallback.find((s) => s.value === score)
  )

  const selectedPlaybooks = scorecardConfig.config_cids?.map((playbookCid) => ({
    value: playbookCid,
    label: data.playbooks.find((playbook) => playbook.value === playbookCid)?.label,
  }))

  const selectedAgents = scorecardConfig.users?.map((user) => ({
    value: user.id,
    label: data.agents.find((agent) => agent.value === user.id)?.label,
  }))

  const selectedTags = scorecardConfig.tags?.map((tag) => ({
    value: tag.id,
    label: data.tags
      .flatMap((tagCat) => tagCat.options)
      .find((tagCatTag) => tagCatTag.value === tag.id)?.label, // :(
  }))
  const selectedDispositions = scorecardConfig.dispositions?.map((disposition) => ({
    value: disposition,
    label: disposition,
  }))

  const selectedVoipCampaigns = scorecardConfig.voip_campaigns?.map((campaign) => {
    return {
      value: campaign.id,
      label: data.allVoipCampaigns?.find((voipCampaign) => voipCampaign.id === campaign.id)?.name,
    }
  })

  const settingsInitialValues = {
    organization_id: scorecardConfig?.organization_id || '',
    improvement_threshold: scorecardConfig?.improvement_threshold || 70,
    meets_threshold: scorecardConfig?.meets_threshold || 80,
    exceeds_threshold: scorecardConfig?.exceeds_threshold || 90,
    required_score_count: scorecardConfig?.required_score_count || 0,
    required_manual_task_count: scorecardConfig?.required_manual_task_count || 0,
    scorecard_frequency: scorecardConfig?.scorecard_frequency || 'monthly',
    min_duration_in_minutes: Math.round(scorecardConfig?.min_duration_in_seconds) / 60 || 1,
    max_duration_in_minutes: Math.round(scorecardConfig?.max_duration_in_seconds) / 60 || undefined,
    shell_prompt: scorecardConfig?.shell_prompt || '',
    eligibility_prompt_id: scorecardConfig?.eligibility_prompt_id,
    should_eligibility_respond_yes: getStringValueFromBoolean(
      scorecardConfig.should_eligibility_respond_yes,
      ''
    ),
    scores_to_fallback: selectedScoresToFallback || null,
    ...getEligibilityPromptValues(eligibilityPrompt),
    // As in the criteria form: an unresolved legacy model name keeps the id unset so the required rule fires,
    // instead of silently re-pointing the eligibility prompt at the default.
    llm_model_id:
      eligibilityPrompt?.llm_model_id ||
      (isEmpty(eligibilityPrompt?.model) ? defaultQaCopilotModelId : null),
    fallback_initial_model_id: scorecardConfig?.fallback_initial_model_id || defaultFallbackModelId,
    prompt_id: eligibilityPrompt?.id || null,
    system_prompt: eligibilityPrompt?.system_prompt || '',
    max_response_tokens: eligibilityPrompt?.max_response_tokens || null,
    call_id: '',
    expected_response: '',
    users: selectedAgents || [],
    users_inclusive: scorecardConfig?.users_inclusive?.toString() || 'true',
    tags: selectedTags || [],
    tags_inclusive: scorecardConfig?.tags_inclusive?.toString() || 'true',
    playbooks: selectedPlaybooks || [],
    multicall: scorecardConfig?.multicall || false,
    multicall_same_user: scorecardConfig?.multicall_same_user ?? false,
    ignore_scored_status: scorecardConfig?.ignore_scored_status || false,
    dispositions_inclusive: scorecardConfig?.dispositions_inclusive?.toString() || 'true',
    dispositions: selectedDispositions || [],
    distribute_evenly: scorecardConfig?.distribute_evenly ?? true,
    voip_campaigns: selectedVoipCampaigns || [],
    voip_campaigns_inclusive: scorecardConfig?.voip_campaigns_inclusive?.toString() || 'true',
    call_type: scorecardConfig?.call_type || 'call',
    min_message_count: scorecardConfig?.min_message_count || null,
    max_message_count: scorecardConfig?.max_message_count || null,
    monthly_cutoff_day: scorecardConfig?.monthly_cutoff_day ?? null,
  }

  const handleCloseModal = () => {
    if (formikDirtyRef.current) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure you want to leave without saving?')) {
        dispatch(closeModal())
      }
    } else {
      dispatch(closeModal())
    }
  }

  const handleDeleteScorecardConfig = () => {
    dispatch(deleteScorecardConfig(scorecardConfig, history))
  }

  const handleSaveSettings = (values) => {
    if (isPromptEditor && values.user_prompt !== '') {
      // Where the form never moved the prompt, store the API's own version rather than one rebuilt from the
      // values. The two differ only by defaults the form materializes — a null `side` loading and saving as
      // 'both' — and the scorecard save diffs prompts raw, so a rebuilt one reads as edited and gets POSTed:
      // the exact write the deprecated-model block just decided this save wasn't making.
      const newPrompt = eligibilityPromptHasChanges({
        pristinePrompt: pristineEligibilityPrompt,
        values,
      })
        ? withoutLegacyModel({
            ...eligibilityPrompt,
            ...getEligibilityPromptPayload(values),
            feature: 'qa_copilot_eligibility', // prompt API requires a feature
            scid: 'eligibility', // this is so we can save this prompt with the rest of them
          })
        : pristineEligibilityPrompt
      if (eligibilityPrompt?.id !== null) {
        const updatedPrompts = updatePromptHelper(prompts, newPrompt)
        dispatch(setPrompts(updatedPrompts))
      } else {
        dispatch(addPrompt(newPrompt))
      }
    }
    // Clear eligibility prompt if we remove the user prompt
    const emptyEligibilityPrompt = isBaltoAdmin && values.user_prompt === ''

    if (emptyEligibilityPrompt) {
      // The prompt has to leave the store too: the save links the scorecard to whatever still carries the
      // 'eligibility' scid, so leaving it there hands back the id nulled just below.
      dispatch(setPrompts(removePromptHelper(prompts, 'eligibility')))
    }

    dispatch(
      updateSettings({
        ...values,
        organization_id: values.organization_id,
        users: !isEmpty(values.users) ? values.users.map((user) => ({ id: user.value })) : [],
        users_inclusive: values.users_inclusive === 'true' || false,
        tags: !isEmpty(values.tags) ? values.tags.map((tag) => ({ id: tag.value })) : [],
        tags_inclusive: values.tags_inclusive === 'true' || false,
        dispositions_inclusive: values.dispositions_inclusive === 'true' || false,
        dispositions: !isEmpty(values?.dispositions)
          ? values.dispositions.map((disposition) => disposition.value)
          : [],
        voip_campaigns: !isEmpty(values?.voip_campaigns)
          ? values.voip_campaigns.map((campaign) => ({
              id: campaign.value,
            }))
          : [],
        voip_campaigns_inclusive: values.voip_campaigns_inclusive === 'true' || false,
        config_cids: !isEmpty(values.playbooks)
          ? values.playbooks.map((playbookCid) => playbookCid.value)
          : [],
        required_score_count: values.required_score_count || 0,
        required_manual_task_count: values.required_manual_task_count || 0,
        scorecard_frequency: values.scorecard_frequency || 'monthly',
        min_duration_in_seconds: Math.round(values.min_duration_in_minutes) * 60 || 1,
        max_duration_in_seconds: Math.round(values.max_duration_in_minutes) * 60 || undefined,
        should_eligibility_respond_yes: values.should_eligibility_respond_yes === 'true',
        eligibility_prompt_id: emptyEligibilityPrompt ? null : values.eligibility_prompt_id,
        scores_to_fallback: !isEmpty(values.scores_to_fallback)
          ? values.scores_to_fallback.map((score) => score.value)
          : null,
        fallback_initial_model_id: !isEmpty(values.scores_to_fallback)
          ? values.fallback_initial_model_id
          : null,
        multicall: values.multicall,
        multicall_same_user: values.multicall_same_user,
        ignore_scored_status: values.ignore_scored_status,
        distribute_evenly: values.distribute_evenly,
        call_type: values.call_type,
        min_message_count: values.min_message_count || null,
        max_message_count: values.max_message_count || null,
        monthly_cutoff_day:
          values.distribute_evenly === true &&
          values.scorecard_frequency === 'monthly' &&
          values.monthly_cutoff_day
            ? Number(values.monthly_cutoff_day)
            : null,
      })
    )
    dispatch(closeModal())
  }

  return (
    <Formik
      initialValues={settingsInitialValues}
      validationSchema={getSettingsValidationSchema({ isPromptEditor })}
      enableReinitialize
      validateOnMount
      onSubmit={handleSaveSettings}
    >
      {({ values, errors, handleSubmit, dirty, setFieldValue }) => {
        formikDirtyRef.current = dirty

        // A deprecated eligibility model can't be saved (the server 422s), so block it in
        // the UI. Only relevant when this save actually rewrites the prompt: a save skips
        // the POST for a prompt that still matches the version the API gave us, so the rest
        // of the settings stay editable on a scorecard whose model was deprecated under it.
        const eligibilityBlocked = eligibilityModelBlocksSave({
          isPromptEditor,
          userPrompt: values.user_prompt,
          promptChanged: eligibilityPromptHasChanges({
            pristinePrompt: pristineEligibilityPrompt,
            values,
          }),
          options: qaCopilotModelOptions,
          value: values.llm_model_id,
        })

        // Same rule for the fallback model, against the id the config already carries — see
        // `fallbackModelBlocksSave` for why a config field can't reuse the prompt's "was it
        // edited" test.
        const fallbackBlocked = fallbackModelBlocksSave({
          scoresToFallback: values.scores_to_fallback,
          savedValue: scorecardConfig?.fallback_initial_model_id,
          options: copilotFallbackModelOptions,
          value: values.fallback_initial_model_id,
        })

        return (
          <BasicModal
            className="qa-copilot-settings-modal"
            title="General Settings"
            onClose={handleCloseModal}
            show={currentlyOpenModalId === modalId}
            size="small"
            scrolling
            footer={
              <div className="flex-align-center flex-end small-gap">
                <Button
                  onClick={handleCloseModal}
                  disabled={isOutdatedScorecard}
                  data-testid="close-modal"
                >
                  Discard
                </Button>
                <Button
                  brand
                  onClick={handleSubmit}
                  disabled={
                    !isEmpty(errors) || isOutdatedScorecard || eligibilityBlocked || fallbackBlocked
                  }
                  disabledTooltip={
                    eligibilityBlocked || fallbackBlocked
                      ? 'Switch to a supported model to save'
                      : 'Must fix errors to submit'
                  }
                  data-testid="qa-copilot-settings-submit-button"
                >
                  Update
                </Button>
              </div>
            }
          >
            <QACopilotSettingsForm
              values={values}
              errors={errors}
              handleSubmit={handleSubmit}
              handleDeleteScorecardConfig={handleDeleteScorecardConfig}
              handleCloseModal={handleCloseModal}
              isBaltoAdmin={isBaltoAdmin}
              isPromptEditor={isPromptEditor}
              eligibilityBlocked={eligibilityBlocked}
              fallbackBlocked={fallbackBlocked}
              isCreate={isCreate}
              setFieldValue={setFieldValue}
              isOutdatedScorecard={isOutdatedScorecard}
            />
          </BasicModal>
        )
      }}
    </Formik>
  )
}
